import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { shoesAPI } from '../api';
import { useTranslation } from '../i18n';
import { toast } from '../components/common/Notifier';
import { formatPace } from '../utils/formatPace';
import { Link } from 'react-router-dom';

const EMPTY = {
  brand: '', model: '', nickname: '', kind: 'daily', first_used_at: '',
  initial_km: 0, max_km: 700, is_default: false, notes: '',
  weight_g: '', price: '', currency: 'EUR',
};
const KINDS = ['daily', 'tempo', 'race', 'trail'];
const CURRENCY_SYMBOL = { EUR: '€', USD: '$', UAH: '₴' };
const NUM_KEYS = new Set(['total_km', 'weight_g', 'price', 'cost_per_km']);
const MAX_PHOTOS = 5;

const field = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500';
const label = 'block text-xs text-gray-500 mb-1';
const iconBtn = 'px-2 py-1.5 rounded-md hover:bg-gray-100';

const calcPct = (s) =>
  Number(s.max_km) > 0 ? Math.min(100, Math.round((Number(s.total_km) / Number(s.max_km)) * 100)) : 0;

// состояние, которое переживает перезагрузку страницы
const usePersisted = (key, initial) => {
  const [value, setValue] = useState(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw === null ? initial : JSON.parse(raw);
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* ignore */ }
  }, [key, value]);
  return [value, setValue];
};

const sortValue = (s, k) => {
  const v = k === 'display' ? `${s.brand} ${s.model}` : s[k];
  if (v == null || v === '') return null;
  return NUM_KEYS.has(k) ? Number(v) : String(v).toLowerCase();
};

const Modal = ({ onClose, children, z = 'z-50', maxW = 'max-w-lg' }) => (
  <div
    role="dialog"
    aria-modal="true"
    className={`fixed inset-0 bg-black/50 ${z} flex items-center justify-center p-4`}
    onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
  >
    <div className={`bg-white rounded-xl shadow-lg w-full ${maxW} max-h-[90vh] overflow-y-auto`}>
      {children}
    </div>
  </div>
);

const ShoeCard = ({ shoe, onEdit, onOpen, onRetire, onRestore, onSetDefault, t }) => {
  const pct = calcPct(shoe);
  const bar = pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-green-500';
  const title = shoe.nickname || `${shoe.brand} ${shoe.model}`;
  const cur = CURRENCY_SYMBOL[shoe.currency];
  return (
    <div className={`bg-white rounded-xl shadow p-4 flex gap-4 ${shoe.retired_at ? 'opacity-60' : ''}`}>
      <button
        type="button"
        onClick={() => onOpen(shoe)}
        aria-label={title}
        className="w-24 h-24 shrink-0 rounded-lg bg-gray-100 overflow-hidden flex items-center justify-center text-4xl hover:opacity-80"
      >
        {shoe.photo_url ? <img src={shoe.photo_url} alt="" className="w-full h-full object-cover" /> : '👟'}
      </button>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-gray-800 truncate">
          <button type="button" onClick={() => onOpen(shoe)} className="hover:text-blue-700 text-left">
            {title}
          </button>
          {shoe.is_default && <span className="ml-2 text-xs text-blue-600">★ {t('shoes.default')}</span>}
        </div>
        {shoe.nickname && <div className="text-sm text-gray-500 truncate">{shoe.brand} {shoe.model}</div>}
        <div className="text-xs text-gray-400 mt-0.5">
          {t(`shoes.kind_${shoe.kind}`)}{shoe.first_used_at && ` · ${shoe.first_used_at}`}
        </div>
        <div className="text-xs text-gray-400">
          {shoe.weight_g && `${shoe.weight_g} ${t('common.g')}`}
          {shoe.price && ` · ${shoe.price} ${cur}`}
          {shoe.cost_per_km && ` · ${shoe.cost_per_km} ${cur}/${t('common.km')}`}
        </div>

        <div className="mt-2">
          <div className="flex justify-between text-sm">
            <span className="font-medium">{shoe.total_km} / {shoe.max_km} {t('common.km')}</span>
            <span className="text-gray-500">{pct}%</span>
          </div>
          <div
            className="h-2 bg-gray-100 rounded-full overflow-hidden"
            role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}
          >
            <div className={`h-full ${bar}`} style={{ width: `${pct}%` }} />
          </div>
          {shoe.is_worn_out && !shoe.retired_at ? (
            <div className="text-xs text-red-600 mt-1">⚠️ {t('shoes.worn_out')}</div>
          ) : pct >= 80 && !shoe.retired_at ? (
            <div className="text-xs text-amber-600 mt-1">{t('shoes.replace_soon')}</div>
          ) : null}
        </div>

        <div className="mt-2 flex flex-wrap gap-1 text-sm -ml-2">
          <button onClick={() => onEdit(shoe)} className={`${iconBtn} text-blue-600 hover:text-blue-800`}>
            ✏️ {t('profile.edit')}
          </button>
          {shoe.retired_at ? (
            <button onClick={() => onRestore(shoe)} className={`${iconBtn} text-green-600 hover:text-green-800`}>
              ♻️ {t('shoes.restore')}
            </button>
          ) : (
            <>
              {!shoe.is_default && (
                <button
                  onClick={() => onSetDefault(shoe)}
                  title={t('shoes.make_default')}
                  aria-label={t('shoes.make_default')}
                  className={`${iconBtn} text-gray-500 hover:text-blue-600`}
                >
                  ☆
                </button>
              )}
              <button onClick={() => onRetire(shoe)} className={`${iconBtn} text-gray-500 hover:text-gray-700`}>
                📦 {t('shoes.retire')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export const ShoesPage = () => {
  const { t } = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;

  const [shoes, setShoes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState(null);            // null = модалка формы закрыта
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const initialRef = useRef('');                     // снимок формы для проверки «есть ли несохранённое»

  const [viewId, setViewId] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [undo, setUndo] = useState(null);            // последняя списанная пара

  const [filter, setFilter] = usePersisted('shoes.filter', 'all');
  const [showTable, setShowTable] = usePersisted('shoes.showTable', false);
  const [sort, setSort] = usePersisted('shoes.sort', { key: 'total_km', dir: 'desc' });

  const editing = shoes.find((s) => s.id === form?.id);
  const photos = editing?.photos || [];
  const slotsLeft = MAX_PHOTOS - photos.length - files.length;
  const viewShoe = shoes.find((s) => s.id === viewId);

  const activeShoes = shoes.filter((s) => !s.retired_at);
  const archiveShoes = shoes.filter((s) => s.retired_at);
  const visible =
    filter === 'archive' ? archiveShoes
      : filter === 'all' ? activeShoes
        : activeShoes.filter((s) => s.kind === filter);

  const sorted = [...visible].sort((a, b) => {
    const av = sortValue(a, sort.key);
    const bv = sortValue(b, sort.key);
    if (av === null && bv === null) return 0;
    if (av === null) return 1;               // пустые всегда внизу
    if (bv === null) return -1;
    const r = typeof av === 'string' ? av.localeCompare(bv) : av - bv;
    return sort.dir === 'asc' ? r : -r;
  });

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }));

  const totalKm = visible.reduce((s, x) => s + (Number(x.total_km) || 0), 0);
  const totalsByCur = visible.reduce((acc, x) => {
    if (x.price) acc[x.currency] = (acc[x.currency] || 0) + Number(x.price);
    return acc;
  }, {});

  const dirty = !!form && (JSON.stringify(form) !== initialRef.current || files.length > 0);

  const previews = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);
  useEffect(() => () => previews.forEach((u) => URL.revokeObjectURL(u)), [previews]);

  const load = useCallback(async () => {
    try {
      const { data } = await shoesAPI.list();
      setShoes(data.results ?? data);
    } catch {
      toast.error(tRef.current('error.general'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // история пробегов — для модалки просмотра
  useEffect(() => {
    if (!viewId) { setHistory([]); return; }
    let cancelled = false;
    setHistoryLoading(true);
    shoesAPI.history(viewId)
      .then(({ data }) => { if (!cancelled) setHistory(data); })
      .catch(() => { if (!cancelled) setHistory([]); })
      .finally(() => { if (!cancelled) setHistoryLoading(false); });
    return () => { cancelled = true; };
  }, [viewId]);

  // снекбар «Отменить» пропадает сам
  useEffect(() => {
    if (!undo) return;
    const id = setTimeout(() => setUndo(null), 6000);
    return () => clearTimeout(id);
  }, [undo]);

  // блокируем скролл страницы под модалками
  const modalOpen = !!form || !!viewShoe;
  useEffect(() => {
    if (!modalOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [modalOpen]);

  const openForm = (shoe) => {
    const next = shoe
      ? { ...EMPTY, ...shoe, first_used_at: shoe.first_used_at || '' }
      : { ...EMPTY };
    initialRef.current = JSON.stringify(next);
    setFiles([]);
    setForm(next);
  };

  const closeForm = useCallback((force = false) => {
    if (!force && dirty && !window.confirm(tRef.current('shoes.discard_confirm'))) return;
    setForm(null);
    setFiles([]);
  }, [dirty]);

  const closeView = useCallback(() => {
    setLightbox(null);
    setViewId(null);
  }, []);

  // Escape: лайтбокс → форма → просмотр; стрелки листают лайтбокс
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (lightbox !== null) setLightbox(null);
        else if (form) closeForm();
        else if (viewId) closeView();
        return;
      }
      if (lightbox !== null && viewShoe?.photos?.length) {
        const n = viewShoe.photos.length;
        if (e.key === 'ArrowRight') setLightbox((i) => (i + 1) % n);
        if (e.key === 'ArrowLeft') setLightbox((i) => (i - 1 + n) % n);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, form, viewId, viewShoe, closeForm, closeView]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!(Number(form.max_km) > 0)) {
      toast.error(t('shoes.max_km_invalid'));
      return;
    }
    setSaving(true);
    try {
      const isNew = !form.id;
      const fd = new FormData();
      ['brand', 'model', 'kind', 'initial_km', 'max_km', 'currency'].forEach((k) => fd.append(k, form[k]));
      // необязательные поля отправляем и пустыми, чтобы их можно было очистить
      ['nickname', 'first_used_at', 'weight_g', 'price', 'notes'].forEach((k) => fd.append(k, form[k] ?? ''));
      fd.append('is_default', form.is_default ? 'true' : 'false');

      const { data: saved } = isNew ? await shoesAPI.create(fd) : await shoesAPI.update(form.id, fd);
      // пара уже создана — дальше это редактирование, повторное сохранение не задвоит её
      if (isNew) setForm((f) => ({ ...f, id: saved.id }));

      const res = await Promise.allSettled(files.map((f) => shoesAPI.addPhoto(saved.id, f)));
      const failed = res.filter((r) => r.status === 'rejected').length;
      if (failed) {
        setFiles(files.filter((_, i) => res[i].status === 'rejected'));
        toast.error(`${t('error.general')} (${failed})`);
        load();
        return;                                  // модалка остаётся открытой
      }

      toast.success(t('common.success'));
      if (isNew) setFilter('all');
      closeForm(true);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error.general'));
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoMain = async (photoId) => {
    try {
      await shoesAPI.setMainPhoto(form.id, photoId);
      load();
    } catch {
      toast.error(t('error.general'));
    }
  };

  const handlePhotoRemove = async (photoId) => {
    if (!window.confirm(t('shoes.confirm_remove_photo'))) return;
    try {
      await shoesAPI.removePhoto(form.id, photoId);
      load();
    } catch {
      toast.error(t('error.general'));
    }
  };

  const patchShoe = async (shoe, entries) => {
    const fd = new FormData();
    Object.entries(entries).forEach(([k, v]) => fd.append(k, v));
    await shoesAPI.update(shoe.id, fd);
  };

  const handleRetire = async (shoe) => {
    try {
      await patchShoe(shoe, { retired_at: new Date().toISOString().slice(0, 10), is_default: 'false' });
      setUndo(shoe);
      load();
    } catch {
      toast.error(t('error.general'));
    }
  };

  const handleRestore = async (shoe) => {
    try {
      await patchShoe(shoe, { retired_at: '' });   // пустое значение сбрасывает дату списания
      setUndo(null);
      setFilter('all');
      load();
    } catch {
      toast.error(t('error.general'));
    }
  };

  const handleSetDefault = async (shoe) => {
    try {
      await patchShoe(shoe, { is_default: 'true' });
      load();
    } catch {
      toast.error(t('error.general'));
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const FILTERS = [
    { key: 'all', label: t('shoes.filter_all'), count: activeShoes.length },
    ...KINDS.map((k) => ({
      key: k,
      label: t(`shoes.kind_${k}`),
      count: activeShoes.filter((s) => s.kind === k).length,
    })),
    { key: 'archive', label: `📦 ${t('shoes.archive')}`, count: archiveShoes.length },
  ];

  const TABLE_COLS = [
    ['display', 'shoes.title_col'],
    ['kind', 'shoes.kind'],
    ['first_used_at', 'shoes.first_used'],
    ['total_km', 'common.km'],
    ['weight_g', 'shoes.weight'],
    ['price', 'shoes.price'],
    ['cost_per_km', 'shoes.cost_per_km'],
  ];

  if (loading) return <div className="text-gray-600">{t('common.loading')}</div>;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Шапка */}
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold text-gray-800">👟 {t('shoes.title')}</h1>

        <div className="flex flex-wrap justify-center gap-2 flex-1">
          {FILTERS.map((f) => {
            if (f.count === 0 && f.key !== 'all' && f.key !== filter) return null;
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                  filter === f.key ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 shadow-sm hover:bg-blue-50'
                }`}
              >
                {f.label} <span className="opacity-60">{f.count}</span>
              </button>
            );
          })}
        </div>

        <button
          onClick={() => openForm(null)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold text-sm"
        >
          + {t('shoes.add')}
        </button>
      </div>

      {/* Пусто */}
      {visible.length === 0 && (
        <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">
          {filter === 'archive' ? t('shoes.archive_empty') : t('shoes.empty')}
        </div>
      )}

      {/* Карточки (та же сортировка, что и в таблице) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {sorted.map((s) => (
          <ShoeCard
            key={s.id}
            shoe={s}
            onEdit={openForm}
            onOpen={(x) => setViewId(x.id)}
            onRetire={handleRetire}
            onRestore={handleRestore}
            onSetDefault={handleSetDefault}
            t={t}
          />
        ))}
      </div>

      {/* Таблица */}
      {visible.length > 0 && (
        <div className="space-y-3">
          <button
            onClick={() => setShowTable((v) => !v)}
            className="text-sm font-medium text-blue-600 hover:text-blue-800"
          >
            📋 {showTable ? t('shoes.hide_table') : t('shoes.show_table')}
          </button>

          {showTable && (
            <div className="bg-white rounded-xl shadow overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                  <tr>
                    <th className="p-3 text-left w-12"></th>
                    {TABLE_COLS.map(([key, labelKey]) => (
                      <th
                        key={key}
                        scope="col"
                        aria-sort={sort.key === key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                        className="p-3 text-left whitespace-nowrap"
                      >
                        <button type="button" onClick={() => toggleSort(key)} className="uppercase hover:text-gray-800">
                          {t(labelKey)}
                          {sort.key === key && (sort.dir === 'asc' ? ' ▲' : ' ▼')}
                        </button>
                      </th>
                    ))}
                    <th className="p-3 text-left">%</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((s) => {
                    const pct = calcPct(s);
                    const cur = CURRENCY_SYMBOL[s.currency];
                    return (
                      <tr
                        key={s.id}
                        onClick={() => setViewId(s.id)}
                        className={`border-t cursor-pointer hover:bg-blue-50 ${s.retired_at ? 'opacity-60' : ''}`}
                      >
                        <td className="p-2">
                          <div className="w-9 h-9 rounded bg-gray-100 overflow-hidden flex items-center justify-center">
                            {s.photo_url ? <img src={s.photo_url} alt="" className="w-full h-full object-cover" /> : '👟'}
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="font-medium text-gray-800">{s.nickname || `${s.brand} ${s.model}`}</div>
                          {s.nickname && <div className="text-xs text-gray-400">{s.brand} {s.model}</div>}
                        </td>
                        <td className="p-3">{t(`shoes.kind_${s.kind}`)}</td>
                        <td className="p-3 whitespace-nowrap">{s.first_used_at || '—'}</td>
                        <td className="p-3 whitespace-nowrap">{s.total_km} / {s.max_km}</td>
                        <td className="p-3">{s.weight_g ? `${s.weight_g} ${t('common.g')}` : '—'}</td>
                        <td className="p-3 whitespace-nowrap">{s.price ? `${s.price} ${cur}` : '—'}</td>
                        <td className="p-3 whitespace-nowrap">{s.cost_per_km ? `${s.cost_per_km} ${cur}` : '—'}</td>
                        <td className={`p-3 font-medium ${pct >= 100 ? 'text-red-600' : pct >= 80 ? 'text-amber-600' : 'text-green-600'}`}>
                          {pct}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 bg-gray-50 font-semibold text-gray-700">
                    <td className="p-3"></td>
                    <td className="p-3">{t('shoes.total')} ({visible.length})</td>
                    <td className="p-3"></td>
                    <td className="p-3"></td>
                    <td className="p-3 whitespace-nowrap">{Math.round(totalKm * 10) / 10} {t('common.km')}</td>
                    <td className="p-3"></td>
                    <td className="p-3 whitespace-nowrap">
                      {Object.entries(totalsByCur)
                        .map(([c, v]) => `${Math.round(v * 100) / 100} ${CURRENCY_SYMBOL[c]}`)
                        .join(' + ') || '—'}
                    </td>
                    <td className="p-3"></td>
                    <td className="p-3"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}

      {/* === Модалка формы === */}
      {form && (
        <Modal onClose={() => closeForm()} maxW="max-w-2xl">
          <form onSubmit={handleSave}>
            <div className="flex justify-between items-center px-6 pt-5 pb-3">
              <h2 className="font-semibold text-lg">
                {form.id ? `✏️ ${t('profile.edit')}` : `+ ${t('shoes.add')}`}
              </h2>
              <button type="button" onClick={() => closeForm()} aria-label={t('common.cancel')}
                      className="text-gray-400 hover:text-gray-600 px-2">✕</button>
            </div>

            <div className="px-6 pb-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className={label}>{t('shoes.brand')}</label>
                  <input className={field} value={form.brand} onChange={set('brand')} required autoFocus />
                </div>
                <div>
                  <label className={label}>{t('shoes.model')}</label>
                  <input className={field} value={form.model} onChange={set('model')} required />
                </div>
                <div>
                  <label className={label}>{t('shoes.nickname')}</label>
                  <input className={field} value={form.nickname} onChange={set('nickname')} />
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <label className={label}>{t('shoes.kind')}</label>
                  <select className={field} value={form.kind} onChange={set('kind')}>
                    {KINDS.map((k) => <option key={k} value={k}>{t(`shoes.kind_${k}`)}</option>)}
                  </select>
                </div>
                <div>
                  <label className={label}>{t('shoes.first_used')}</label>
                  <input type="date" className={field} value={form.first_used_at} onChange={set('first_used_at')} />
                </div>
                <div>
                  <label className={label}>{t('shoes.initial_km')}</label>
                  <input type="number" step="0.1" min="0" inputMode="decimal" className={field}
                         value={form.initial_km} onChange={set('initial_km')} />
                </div>
                <div>
                  <label className={label}>{t('shoes.max_km')}</label>
                  <input type="number" step="10" min="1" inputMode="numeric" className={field}
                         value={form.max_km} onChange={set('max_km')} required />
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div>
                  <label className={label}>{t('shoes.weight')} ({t('common.g')})</label>
                  <input type="number" min="0" inputMode="numeric" className={field}
                         value={form.weight_g} onChange={set('weight_g')} />
                </div>
                <div>
                  <label className={label}>{t('shoes.price')}</label>
                  <input type="number" step="0.01" min="0" inputMode="decimal" className={field}
                         value={form.price} onChange={set('price')} />
                </div>
                <div>
                  <label className={label}>{t('shoes.currency')}</label>
                  <select className={field} value={form.currency} onChange={set('currency')}>
                    <option value="EUR">€ EUR</option>
                    <option value="USD">$ USD</option>
                    <option value="UAH">₴ UAH</option>
                  </select>
                </div>
              </div>

              <div>
                <label className={label}>{t('shoes.notes')}</label>
                <textarea className={field} rows={2} value={form.notes} onChange={set('notes')} />
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!form.is_default}
                  onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
                />
                ★ {t('shoes.default')}
              </label>

              {/* Фото: до 5 */}
              <div>
                <div className={label}>{t('shoes.photos')} ({photos.length + files.length}/{MAX_PHOTOS})</div>
                <div className="flex flex-wrap gap-2">
                  {photos.map((p) => (
                    <div key={p.id} className="relative w-20 h-20 rounded-lg overflow-hidden bg-gray-100">
                      <img src={p.url} alt="" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => handlePhotoMain(p.id)} title={t('shoes.make_main')}
                              aria-label={t('shoes.make_main')}
                              className={`absolute top-0.5 left-0.5 text-xs px-1.5 py-0.5 rounded ${p.is_main ? 'bg-yellow-400' : 'bg-white/80'}`}>★</button>
                      <button type="button" onClick={() => handlePhotoRemove(p.id)} aria-label="✕"
                              className="absolute top-0.5 right-0.5 text-xs px-1.5 py-0.5 rounded bg-white/80 text-red-600">✕</button>
                    </div>
                  ))}
                  {files.map((f, i) => (
                    <div key={`${f.name}-${i}`} className="relative w-20 h-20 rounded-lg overflow-hidden bg-gray-100">
                      <img src={previews[i]} alt="" className="w-full h-full object-cover opacity-70" />
                      <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} aria-label="✕"
                              className="absolute top-0.5 right-0.5 text-xs px-1.5 py-0.5 rounded bg-white/80 text-red-600">✕</button>
                    </div>
                  ))}
                  {slotsLeft > 0 && (
                    <label className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center text-2xl text-gray-400 cursor-pointer hover:border-blue-400">
                      +
                      <input
                        type="file" accept="image/*" multiple className="hidden"
                        onChange={(e) => {
                          setFiles([...files, ...Array.from(e.target.files).slice(0, slotsLeft)]);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  )}
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t px-6 py-3 flex gap-2 rounded-b-xl">
              <button type="submit" disabled={saving}
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold text-sm disabled:opacity-50">
                {saving ? t('common.loading') : t('common.save')}
              </button>
              <button type="button" onClick={() => closeForm()}
                      className="px-4 py-2 rounded-lg text-sm bg-gray-100 hover:bg-gray-200">
                {t('common.cancel')}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* === Модалка просмотра === */}
      {viewShoe && (
        <Modal onClose={closeView}>
          <div className="p-5 space-y-3">
            <div className="flex justify-between items-start">
              <div>
                <div className="font-semibold text-lg">
                  {viewShoe.nickname || `${viewShoe.brand} ${viewShoe.model}`}
                </div>
                <div className="text-sm text-gray-500">{viewShoe.brand} {viewShoe.model}</div>
                <div className="text-xs text-gray-400 mt-0.5">
                  {t(`shoes.kind_${viewShoe.kind}`)}{viewShoe.first_used_at && ` · ${viewShoe.first_used_at}`}
                </div>
              </div>
              <button onClick={closeView} aria-label={t('common.cancel')}
                      className="text-gray-400 hover:text-gray-600 px-2">✕</button>
            </div>

            <div className="grid grid-cols-3 gap-2">
              {(viewShoe.photos || []).map((p, i) => (
                <button
                  type="button" key={p.id} onClick={() => setLightbox(i)}
                  className={`aspect-square rounded-lg overflow-hidden bg-gray-100 hover:opacity-80 ${p.is_main ? 'ring-2 ring-yellow-400' : ''}`}
                >
                  <img src={p.url} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
              {(!viewShoe.photos || viewShoe.photos.length === 0) && (
                <div className="col-span-3 text-center text-4xl py-6">👟</div>
              )}
            </div>

            <div className="text-sm text-gray-700 space-y-1">
              <div>{viewShoe.total_km} / {viewShoe.max_km} {t('common.km')} · {calcPct(viewShoe)}%</div>
              {viewShoe.weight_g && <div>⚖️ {viewShoe.weight_g} {t('common.g')}</div>}
              {viewShoe.price && (
                <div>
                  💳 {viewShoe.price} {CURRENCY_SYMBOL[viewShoe.currency]}
                  {viewShoe.cost_per_km &&
                    ` (${viewShoe.cost_per_km} ${CURRENCY_SYMBOL[viewShoe.currency]}/${t('common.km')})`}
                </div>
              )}
              {viewShoe.notes && <p className="text-gray-500 italic whitespace-pre-wrap">{viewShoe.notes}</p>}
            </div>

            {/* История пробегов */}
            <div className="border-t pt-3">
              <div className="text-sm font-medium text-gray-700 mb-2">
                📜 {t('shoes.history')} ({history.length})
              </div>
              {historyLoading ? (
                <div className="text-xs text-gray-400">{t('common.loading')}</div>
              ) : history.length === 0 ? (
                <div className="text-xs text-gray-400">{t('shoes.history_empty')}</div>
              ) : (
                <div className="max-h-56 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="text-gray-400 text-left sticky top-0 bg-white">
                      <tr>
                        <th className="py-1">{t('shoes.date')}</th>
                        <th className="py-1">{t('common.km')}</th>
                        <th className="py-1">{t('workouts.pace')}</th>
                        <th className="py-1"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((h) => (
                        <tr key={h.id} className="border-t border-gray-100">
                          <td className="py-1.5 whitespace-nowrap">{h.date}</td>
                          <td className="py-1.5">{h.distance_km}</td>
                          <td className="py-1.5 whitespace-nowrap">
                            {h.avg_pace_min_per_km ? `${formatPace(h.avg_pace_min_per_km)} /${t('common.km')}` : '—'}
                          </td>
                          <td className="py-1.5 text-right">
                            <Link to={`/workouts?date=${h.date}`} className="text-blue-600 hover:text-blue-800 px-2">→</Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Действия */}
            <div className="border-t pt-3 flex gap-2 text-sm">
              <button
                onClick={() => { const s = viewShoe; closeView(); openForm(s); }}
                className="px-3 py-2 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100"
              >
                ✏️ {t('profile.edit')}
              </button>
              {viewShoe.retired_at ? (
                <button
                  onClick={() => { handleRestore(viewShoe); closeView(); }}
                  className="px-3 py-2 rounded-lg bg-green-50 text-green-700 hover:bg-green-100"
                >
                  ♻️ {t('shoes.restore')}
                </button>
              ) : (
                <button
                  onClick={() => { handleRetire(viewShoe); closeView(); }}
                  className="px-3 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
                >
                  📦 {t('shoes.retire')}
                </button>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* === Лайтбокс === */}
      {viewShoe && lightbox !== null && viewShoe.photos?.[lightbox] && (
        <div
          className="fixed inset-0 bg-black/85 z-[60] flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <button onClick={() => setLightbox(null)} aria-label="✕"
                  className="absolute top-4 right-4 text-white text-3xl leading-none hover:text-gray-300">✕</button>

          {viewShoe.photos.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setLightbox((lightbox - 1 + viewShoe.photos.length) % viewShoe.photos.length); }}
                className="absolute left-4 text-white text-4xl px-3 hover:text-gray-300"
              >‹</button>
              <button
                onClick={(e) => { e.stopPropagation(); setLightbox((lightbox + 1) % viewShoe.photos.length); }}
                className="absolute right-4 text-white text-4xl px-3 hover:text-gray-300"
              >›</button>
            </>
          )}

          <img
            src={viewShoe.photos[lightbox].url}
            alt=""
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-4 text-white/70 text-sm">{lightbox + 1} / {viewShoe.photos.length}</div>
        </div>
      )}

      {/* === Снекбар «Отменить» === */}
      {undo && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[70] bg-gray-800 text-white text-sm rounded-lg shadow-lg px-4 py-2 flex items-center gap-3">
          <span>{t('shoes.archived')}</span>
          <button onClick={() => handleRestore(undo)} className="font-semibold text-blue-300 hover:text-blue-200">
            {t('common.undo')}
          </button>
        </div>
      )}
    </div>
  );
};